import { useState, useCallback, useRef } from 'react'
import { callClaudeStreaming } from '@/lib/ai/claude'
import { useAIStore } from '@/stores/aiStore'
import type { DiscoveryPhase, DiscoveryEntity } from '@/components/discovery/discoveryPhases'

export interface DiscoveryContext {
  phase: DiscoveryPhase
  entity: DiscoveryEntity
  existingEntities: Array<{ label: string; type: string }>
  userInput?: string
}

export interface GuidanceResponse {
  questions: string[]
  suggestions: string[]
  tips: string[]
  example?: string
  // Collaborative coaching additions
  framework?: string
  acknowledge?: string
  opportunity?: string
}

// Streaming state for progressive display
export interface StreamingState {
  isStreaming: boolean
  rawText: string
}

// ============================================================================
// COACHING SYSTEM PROMPTS
// ============================================================================

const DIRECTIVE_SYSTEM_PROMPT = `You are an expert business strategy consultant helping entrepreneurs and product managers through the Lean Startup discovery process. Your role is to guide them in filling out their business model canvas, value proposition canvas, and understanding their market.

You should:
1. Ask probing questions to help them think deeply
2. Suggest concrete examples based on their industry
3. Challenge assumptions constructively
4. Keep responses concise and actionable

Always respond in this JSON format:
{
  "questions": ["list of 2-3 guiding questions to help them think deeper"],
  "suggestions": ["list of 2-3 concrete suggestions or examples"],
  "tips": ["1-2 practical tips"],
  "example": "an optional concrete example if helpful"
}

Be warm, encouraging, and practical. Help them move forward rather than getting stuck in analysis paralysis.`

const COLLABORATIVE_SYSTEM_PROMPT = `You are a warm, collaborative product discovery coach with 10+ years helping founders validate ideas. You're not here to test users - you're here to help them think.

Your coaching philosophy:
1. SHARE before you ASK - provide frameworks and context first
2. SCAFFOLD thinking - offer structure before expecting answers
3. ACKNOWLEDGE before SUGGEST - validate what they've done before refining
4. MAKE KNOWLEDGE ACCESSIBLE - surface helpful frameworks visibly
5. RESPECT AUTONOMY - users can always proceed or skip

Your language guidelines:
- Replace "shallow" → "starting point"
- Replace "missing" → "opportunity to add"
- Replace "gap" → "area to strengthen"
- Replace "vague" → "could be more specific"
- Always lead with what's good
- Frame everything as optional enhancement

Always respond in this JSON format:
{
  "framework": "A helpful framework or mental model for thinking about this (e.g., 'Jobs-to-be-Done', 'Target Persona Canvas')",
  "acknowledge": "Warm acknowledgment of where they are in the journey",
  "questions": ["2-3 guiding questions that scaffold their thinking"],
  "suggestions": ["2-3 concrete suggestions framed as opportunities"],
  "tips": ["1-2 practical tips"],
  "example": "A specific, relatable example if helpful",
  "opportunity": "An optional prompt to go deeper: 'If you'd like to strengthen this further, consider...'"
}

Be encouraging, practical, and collaborative. Help them build confidence while moving forward.`

function buildUserPrompt(context: DiscoveryContext): string {
  const { phase, entity, existingEntities, userInput } = context

  let prompt = `I am in the "${phase.title}" phase of my product discovery journey.

I need help defining: **${entity.label}**
Description: ${entity.description}

`

  if (existingEntities.length > 0) {
    prompt += `I have already defined these entities:\n`
    existingEntities.forEach((e) => {
      prompt += `- ${e.type}: ${e.label}\n`
    })
    prompt += '\n'
  } else {
    prompt += `I haven't defined any entities yet.\n\n`
  }

  if (userInput) {
    prompt += `I'm thinking about: "${userInput}"\n\n`
  }

  prompt += `Please help me with guiding questions, suggestions, and tips for defining my ${entity.label}(s).`

  return prompt
}

/**
 * Parse streaming response text into structured guidance
 */
function parseGuidanceResponse(response: string, fallbackTips: string[]): GuidanceResponse {
  try {
    // Clean markdown if present
    let cleaned = response.replace(/```json\s*/gi, '').replace(/```\s*/g, '').trim()
    const startIndex = cleaned.indexOf('{')
    const endIndex = cleaned.lastIndexOf('}')
    if (startIndex !== -1 && endIndex !== -1) {
      cleaned = cleaned.slice(startIndex, endIndex + 1)
    }

    const parsed = JSON.parse(cleaned) as GuidanceResponse
    // Ensure required arrays exist
    return {
      questions: parsed.questions || [],
      suggestions: parsed.suggestions || [],
      tips: parsed.tips || fallbackTips,
      example: parsed.example,
      // Collaborative fields (optional)
      framework: parsed.framework,
      acknowledge: parsed.acknowledge,
      opportunity: parsed.opportunity,
    }
  } catch {
    // If JSON parsing fails, create a structured response from text
    return {
      questions: [response.split('\n')[0] || 'What problem are you trying to solve?'],
      suggestions: [],
      tips: fallbackTips,
    }
  }
}

export function useDiscoveryAssistant() {
  const { apiKey, isConfigured, coachingModel, coachingStyle } = useAIStore()
  const [lastResponse, setLastResponse] = useState<GuidanceResponse | null>(null)
  const [chatResponse, setChatResponse] = useState<string | null>(null)
  const [streamingState, setStreamingState] = useState<StreamingState>({
    isStreaming: false,
    rawText: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Select system prompt based on coaching style
  const systemPrompt = coachingStyle === 'collaborative'
    ? COLLABORATIVE_SYSTEM_PROMPT
    : DIRECTIVE_SYSTEM_PROMPT

  // Get AI guidance for an entity (called explicitly by user, not automatically)
  const getGuidance = useCallback(
    async (context: DiscoveryContext) => {
      if (!isConfigured || !apiKey || isLoading) {
        return
      }

      // Abort any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      setIsLoading(true)
      setError(null)
      setLastResponse(null)
      setChatResponse(null)
      setStreamingState({ isStreaming: true, rawText: '' })

      try {
        const userPrompt = buildUserPrompt(context)
        let streamedText = ''

        const fullResponse = await callClaudeStreaming(
          { apiKey, model: coachingModel, maxTokens: 1024 },
          systemPrompt,
          userPrompt,
          (chunk) => {
            streamedText += chunk
            setStreamingState({ isStreaming: true, rawText: streamedText })
          }
        )

        // Parse the complete response
        const parsed = parseGuidanceResponse(fullResponse, context.entity.tips)
        setLastResponse(parsed)
        setStreamingState({ isStreaming: false, rawText: '' })
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err)
        }
      } finally {
        setIsLoading(false)
        setStreamingState((prev) => ({ ...prev, isStreaming: false }))
      }
    },
    [isConfigured, apiKey, coachingModel, coachingStyle, systemPrompt, isLoading]
  )

  // Ask a specific question to the AI coach
  const askQuestion = useCallback(
    async (question: string, context: DiscoveryContext) => {
      if (!isConfigured || !apiKey || isLoading || !question.trim()) {
        return
      }

      // Abort any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      setIsLoading(true)
      setError(null)
      setChatResponse(null)
      setStreamingState({ isStreaming: true, rawText: '' })

      // Build chat prompt based on coaching style
      const chatPrompt = coachingStyle === 'collaborative'
        ? `You are a warm, collaborative product discovery coach helping entrepreneurs think through their ideas.

The user is in the "${context.phase.title}" phase, working on defining their ${context.entity.label}.

Context about this entity:
- ${context.entity.description}

The user asks: "${question}"

Please provide a helpful, encouraging response. Remember to:
- Acknowledge their question first
- Provide practical guidance
- Offer frameworks or examples if relevant
- Frame suggestions as opportunities, not requirements
- Be warm and supportive`
        : `You are an expert business strategy consultant helping entrepreneurs through the Lean Startup discovery process.

The user is in the "${context.phase.title}" phase, working on defining their ${context.entity.label}.

Context about this entity:
- ${context.entity.description}

The user asks: "${question}"

Please provide a helpful, concise response. Be practical and actionable. If relevant, include examples.`

      try {
        let streamedText = ''

        await callClaudeStreaming(
          { apiKey, model: coachingModel, maxTokens: 1024 },
          chatPrompt,
          question,
          (chunk) => {
            streamedText += chunk
            setStreamingState({ isStreaming: true, rawText: streamedText })
          }
        )

        setChatResponse(streamedText)
        setStreamingState({ isStreaming: false, rawText: '' })
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err)
        }
      } finally {
        setIsLoading(false)
        setStreamingState((prev) => ({ ...prev, isStreaming: false }))
      }
    },
    [isConfigured, apiKey, coachingModel, coachingStyle, isLoading]
  )

  const clearGuidance = useCallback(() => {
    setLastResponse(null)
    setChatResponse(null)
    setStreamingState({ isStreaming: false, rawText: '' })
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }, [])

  return {
    getGuidance,
    askQuestion,
    clearGuidance,
    guidance: lastResponse,
    chatResponse,
    streamingState,
    isLoading,
    error,
    isConfigured,
  }
}

// Pre-defined guidance for when AI is not configured
export function getStaticGuidance(phase: DiscoveryPhase, entity: DiscoveryEntity): GuidanceResponse {
  // Phase-specific questions
  const phaseQuestions: Record<string, Record<string, string[]>> = {
    'customer-discovery': {
      Industry: [
        'What industry category best describes your market?',
        'What is the estimated market size in your target geography?',
        'Is this industry growing, stable, or declining?',
      ],
      CustomerSegment: [
        'Who has the problem you are solving?',
        'What characteristics do your ideal customers share?',
        'Can you describe a specific type of customer in detail?',
      ],
      Persona: [
        'What is a typical day like for this person?',
        'What frustrates them most about the current situation?',
        'What would success look like for them?',
      ],
    },
    'problem-validation': {
      CustomerJob: [
        'What task is the customer trying to accomplish?',
        'Is this a functional, social, or emotional job?',
        'How frequently do they need to do this?',
      ],
      CustomerPain: [
        'What frustrates customers about the current way of doing things?',
        'How severe is this pain (extreme, moderate, or mild)?',
        'What is the cost of this pain (time, money, stress)?',
      ],
      CustomerGain: [
        'What outcome would make your customer delighted?',
        'What would exceed their expectations?',
        'How would they measure success?',
      ],
      Competitor: [
        'What do customers use today to solve this problem?',
        'What are the main alternatives (including doing nothing)?',
        'What do competitors do well? What do they miss?',
      ],
    },
    'value-design': {
      ValueProposition: [
        'What unique value do you offer that others don\'t?',
        'Which customer pains do you alleviate?',
        'Which customer gains do you create?',
      ],
      Channel: [
        'How will customers first hear about you?',
        'How will they evaluate and purchase?',
        'How will you deliver the value?',
      ],
      CustomerRelationship: [
        'What type of relationship do your customers expect?',
        'Self-service, personal assistance, or automated?',
        'How will you retain customers over time?',
      ],
    },
    'solution-design': {
      Product: [
        'What is the core product or service?',
        'What makes it unique in the market?',
        'What is the minimum viable version?',
      ],
      Feature: [
        'What capability directly addresses customer pains?',
        'What is essential vs nice-to-have?',
        'How will users interact with this feature?',
      ],
      UserStory: [
        'As a [persona], I want to [action] so that [benefit]',
        'What are the acceptance criteria?',
        'How complex is this (story points)?',
      ],
      Requirement: [
        'Is this functional or non-functional?',
        'What is the success criteria?',
        'Are there security or performance considerations?',
      ],
    },
    'business-model': {
      RevenueStream: [
        'How will you generate revenue?',
        'What pricing model (subscription, one-time, freemium)?',
        'What are customers willing to pay?',
      ],
      CostStructure: [
        'What are your biggest cost drivers?',
        'Fixed costs vs variable costs?',
        'Are you cost-driven or value-driven?',
      ],
      KeyActivity: [
        'What must you do to deliver value?',
        'What operations are essential?',
        'What can be outsourced?',
      ],
      KeyResource: [
        'What assets are essential to operate?',
        'Physical, intellectual, human, or financial?',
        'What is your unfair advantage?',
      ],
      KeyPartnership: [
        'Who are your key suppliers?',
        'What strategic alliances do you need?',
        'What can partners provide that you can\'t?',
      ],
    },
  }

  const questions = phaseQuestions[phase.id]?.[entity.localName] || [
    `What ${entity.label.toLowerCase()} are you considering?`,
    `Why is this ${entity.label.toLowerCase()} important?`,
    `How does this relate to your customers?`,
  ]

  return {
    questions,
    suggestions: entity.tips,
    tips: [
      'Start with what you know, then validate',
      'It\'s okay to iterate as you learn more',
    ],
  }
}
